import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { env } from '../lib/env.js';

// 50b/N — designer upload page. Public, no Magig account required.
// The magic-link token in the URL is the credential. The page does:
//
//   1. Reads token from ?token=…
//   2. GET /api/designer/brief?token=… → renders the brief
//   3. POST /api/designer/upload?token=… → uploads the file
//   4. On success, swaps to a confirmation panel
//
// Auth note: this route is NOT auth-gated. The api endpoints are
// also intentionally public — they verify the HMAC token instead.

const searchSchema = z.object({
  token: z.string().optional(),
});

interface DesignerBrief {
  brandName: string;
  channelKind: string;
  format: string;
  pillar: string;
  concept: string;
  imageBrief: string;
  captionPreview: string;
  scheduledForLabel: string;
  expiresAtISO: string;
}

export const Route = createFileRoute('/designer/upload')({
  validateSearch: searchSchema,
  component: DesignerUploadPage,
});

function DesignerUploadPage() {
  const { t } = useLingui();

  const { token } = Route.useSearch();
  if (!token) {
    return <ErrorShell title={t`missing link`} detail={t`this page expects a token in the URL.`} />;
  }

  return <DesignerUploadInner token={token} />;
}

function DesignerUploadInner({ token }: { token: string }) {
  const { t } = useLingui();

  const briefQ = useQuery({
    queryKey: ['designer-brief', token],
    queryFn: async (): Promise<DesignerBrief> => {
      const res = await fetch(
        `${env.API_URL}/api/designer/brief?token=${encodeURIComponent(token)}`,
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${detail}`);
      }
      const body = (await res.json()) as { brief: DesignerBrief };
      return body.brief;
    },
    retry: false,
    staleTime: 60_000,
  });

  if (briefQ.isLoading) {
    return <ErrorShell title={t`loading…`} detail={t`pulling the brief mag wrote for you.`} />;
  }
  if (briefQ.isError) {
    return (
      <ErrorShell
        title={t`link not valid`}
        detail={briefQ.error instanceof Error ? briefQ.error.message : 'unknown error'}
      />
    );
  }
  if (!briefQ.data) return null;
  return <UploadFlow token={token} brief={briefQ.data} />;
}

function UploadFlow({ token, brief }: { token: string; brief: DesignerBrief }) {
  const { t } = useLingui();

  const [file, setFile] = useState<File | null>(null);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('pick a file');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${env.API_URL}/api/designer/upload?token=${encodeURIComponent(token)}`,
        { method: 'POST', body: fd },
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${detail}`);
      }
      return (await res.json()) as { outcome: 'attached'; slotId: string; mediaUrl: string };
    },
    onSuccess: () => setDone(true),
  });

  if (done) {
    return (
      <Shell>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>attached</Trans>
        </p>
        <h1 className="mt-2 font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em]">
          <Trans>got it. thanks.</Trans>
        </h1>
        <p className="mag-voice mt-3 text-[15px] leading-[1.55] text-[var(--ink)]/80">
          <Trans>
            “your asset's in. mag's lining it up for{' '}
            <span className="font-semibold">{brief.scheduledForLabel}</span>. nothing else to do.”
          </Trans>
        </p>
        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
          <Trans>you can close this tab.</Trans>
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--plum)]">
        <Trans>↘ brief from mag · {brief.channelKind}</Trans>
      </p>
      <h1 className="mt-2 font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em]">
        {brief.brandName.toLowerCase()} · {brief.format}
      </h1>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
        <Trans>
          scheduled for {brief.scheduledForLabel} · pillar · {brief.pillar.toLowerCase()}
        </Trans>
      </p>

      <BriefBlock label={t`concept`} body={brief.concept} accent />
      <BriefBlock label={t`image brief`} body={brief.imageBrief} />
      <BriefBlock label={t`caption mag wrote`} body={brief.captionPreview} />

      <div
        className={`mt-6 rounded-2xl border-[1.5px] border-dashed bg-[var(--paper)] p-6 text-center transition ${
          file ? 'border-[var(--plum)]' : 'border-[var(--ink)]/30'
        }`}
      >
        <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          {'↘'} <Trans>upload the asset</Trans>
        </p>
        {file ? (
          <div className="mt-3">
            <p className="text-[14px] font-semibold text-[var(--ink)]">{file.name}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              <Trans>
                {(file.size / 1024).toFixed(1)} kb · {file.type || i18n._(msg`unknown`)}
              </Trans>
            </p>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="mt-3 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55 hover:text-[var(--plum)]"
            >
              {'↻'} <Trans>pick a different one</Trans>
            </button>
          </div>
        ) : (
          <div className="mt-3">
            <p className="text-[13px] text-[var(--ink)]/65">
              <Trans>accepted: jpg · png · webp · mp4 · mov · webm — up to 50mb</Trans>
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="btn btn-spell mt-4"
            >
              <Trans>pick a file</Trans>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setFile(f);
          }}
          className="hidden"
        />
      </div>

      {mutation.isError ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--pulse)]">
          {'↘'}{' '}
          <Trans>
            couldn't upload —{' '}
            {mutation.error instanceof Error ? mutation.error.message : i18n._(msg`try again`)}
          </Trans>
        </p>
      ) : null}

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          disabled={!file || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="btn btn-spell disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? <Trans>uploading…</Trans> : <Trans>send to mag</Trans>}
          {!mutation.isPending ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </Shell>
  );
}

function BriefBlock({ label, body, accent }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`mt-4 rounded-xl border bg-white p-4 ${
        accent ? 'border-[var(--ink)] shadow-[3px_3px_0_var(--spell)]' : 'border-[var(--ink)]/15'
      }`}
    >
      <p
        className={`font-mono text-[10px] font-semibold uppercase tracking-[0.1em] ${
          accent ? 'text-[var(--plum)]' : 'text-[var(--ink)]/55'
        }`}
      >
        ↘ {label}
      </p>
      <p className="mt-1.5 whitespace-pre-line text-[13.5px] leading-[1.55] text-[var(--ink)]/85">
        {body}
      </p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <main className="mx-auto max-w-[680px] px-4 py-10 sm:px-6 sm:py-14">{children}</main>
    </div>
  );
}

function ErrorShell({ title, detail }: { title: string; detail: string }) {
  return (
    <Shell>
      <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>designer upload</Trans>
      </p>
      <h1 className="mt-2 font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em]">
        {title}
      </h1>
      <p className="mt-3 text-[15px] leading-[1.55] text-[var(--ink)]/75">{detail}</p>
    </Shell>
  );
}
